import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import US_STATES_TOPOJSON from "@/data/us-states-10m.json";

// The topojson is bundled directly into the JS payload — no network fetch, no
// dependency on static-asset serving. This is intentionally robust so the map
// renders identically in preview, production, mobile, and offline.

const STATUS_STYLE = {
  past: { fill: "#7A8AA0", ring: "rgba(122,138,160,0.35)" },
  current: { fill: "#C97719", ring: "rgba(201,119,25,0.35)" },
  upcoming: { fill: "#0F2447", ring: "rgba(15,36,71,0.35)" },
};

// USPS abbrev → 2-letter code lookup for FIPS
const FIPS_TO_USPS = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE","11":"DC",
  "12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA","20":"KS","21":"KY",
  "22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT",
  "31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND","39":"OH",
  "40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD","47":"TN","48":"TX","49":"UT",
  "50":"VT","51":"VA","53":"WA","54":"WV","55":"WI","56":"WY",
};

// Full state name → USPS 2-letter code (destinations may store the state's full name in
// their `country` field for legacy reasons; we normalise both here).
const NAME_TO_USPS = {
  "alabama":"AL","alaska":"AK","arizona":"AZ","arkansas":"AR","california":"CA","colorado":"CO",
  "connecticut":"CT","delaware":"DE","district of columbia":"DC","florida":"FL","georgia":"GA",
  "hawaii":"HI","idaho":"ID","illinois":"IL","indiana":"IN","iowa":"IA","kansas":"KS",
  "kentucky":"KY","louisiana":"LA","maine":"ME","maryland":"MD","massachusetts":"MA",
  "michigan":"MI","minnesota":"MN","mississippi":"MS","missouri":"MO","montana":"MT",
  "nebraska":"NE","nevada":"NV","new hampshire":"NH","new jersey":"NJ","new mexico":"NM",
  "new york":"NY","north carolina":"NC","north dakota":"ND","ohio":"OH","oklahoma":"OK",
  "oregon":"OR","pennsylvania":"PA","rhode island":"RI","south carolina":"SC","south dakota":"SD",
  "tennessee":"TN","texas":"TX","utah":"UT","vermont":"VT","virginia":"VA","washington":"WA",
  "west virginia":"WV","wisconsin":"WI","wyoming":"WY",
};

const codeFromDestination = (d) => {
  const raw = String(d.state || d.country || "").trim();
  if (!raw) return null;
  // Already a 2-letter USPS code
  if (raw.length === 2) return raw.toUpperCase();
  return NAME_TO_USPS[raw.toLowerCase()] || null;
};

export default function TravelsUSMap({ destinations }) {
  const [hover, setHover] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Only US destinations with coordinates. `coords` is pre-computed here so the
  // Marker prop is a stable reference across renders (avoids child re-renders).
  const valid = useMemo(
    () => (destinations || [])
      .filter((d) => typeof d.lat === "number" && typeof d.lng === "number")
      .map((d) => ({ ...d, coords: [d.lng, d.lat] })),
    [destinations]
  );

  // Set of state codes with at least one destination — for state-level tint
  const touchedStates = useMemo(() => {
    const s = new Set();
    valid.forEach((d) => {
      const code = codeFromDestination(d);
      if (code) s.add(code);
    });
    return s;
  }, [valid]);

  if (valid.length === 0) {
    return (
      <div data-testid="travels-map-empty" className="rounded-sm border border-dashed border-[var(--line)] bg-[var(--bg-alt)] p-16 text-center mb-12">
        <div className="overline mb-3">Journey map</div>
        <p className="text-sm text-[var(--ink-soft)] max-w-md mx-auto">
          Destinations with a state + coordinates will drop pins on the US map here.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="travels-map" className="relative rounded-lg overflow-hidden border border-[var(--line)] mb-14"
      style={{ background: "radial-gradient(ellipse at 50% 30%, #FBF6E5 0%, #F1EDD8 55%, #E7DFC5 100%)" }}>
      <ComposableMap
        projection="geoAlbersUsa"
        width={980}
        height={560}
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <Geographies geography={US_STATES_TOPOJSON}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const fips = String(geo.id).padStart(2, "0");
              const code = FIPS_TO_USPS[fips];
              const isTouched = code && touchedStates.has(code);
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isTouched ? "rgba(201, 119, 25, 0.30)" : "#EFE5C0"}
                  stroke="#B8AA7C"
                  strokeWidth={0.8}
                  onMouseEnter={() => setHover(geo.properties.name)}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    default: { outline: "none", transition: "fill 200ms" },
                    hover: { fill: "rgba(201, 119, 25, 0.45)", outline: "none", cursor: "default" },
                    pressed: { outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>

        {valid.map((d) => {
          const style = STATUS_STYLE[d.status] || STATUS_STYLE.upcoming;
          const isCurrent = d.status === "current";
          return (
            <Marker key={d.id} coordinates={d.coords}
              onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, d })}
              onMouseMove={(e) => setTooltip({ x: e.clientX, y: e.clientY, d })}
              onMouseLeave={() => setTooltip(null)}
              style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" }, pressed: { cursor: "pointer" } }}
            >
              {isCurrent && (
                <>
                  <circle r={22} fill={style.fill} opacity={0.28}>
                    <animate attributeName="r" from="14" to="30" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.5" to="0" dur="2s" repeatCount="indefinite" />
                  </circle>
                </>
              )}
              <circle r={isCurrent ? 11 : 8} fill={style.fill} stroke="#0F2447" strokeWidth={2} />
              {isCurrent && (
                <circle r={4} fill="#0F2447" />
              )}
            </Marker>
          );
        })}
      </ComposableMap>

      {hover && (
        <div className="hidden md:block absolute top-4 left-4 bg-[var(--ink)] text-[var(--bg)] px-3 py-1.5 rounded-sm text-xs shadow-lg pointer-events-none font-mono-acc tracking-wider uppercase">
          {hover}
        </div>
      )}

      {tooltip && (
        <div
          className="fixed z-50 bg-[var(--ink)] text-[var(--bg)] px-3.5 py-2 rounded-sm text-xs shadow-xl pointer-events-none min-w-[160px]"
          style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
        >
          <div className="font-mono-acc text-[10px] tracking-widest uppercase" style={{ color: STATUS_STYLE[tooltip.d.status]?.fill }}>
            {tooltip.d.status}
          </div>
          <div className="font-medium mt-0.5">{tooltip.d.location}</div>
          {tooltip.d.title && <div className="text-white/60 text-[10px] mt-1 italic">{tooltip.d.title}</div>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5 text-xs text-[var(--ink-soft)] bg-[var(--bg-alt)] border-t border-[var(--line)]">
        <span className="inline-flex items-center gap-1.5">
          <span className="relative flex w-3 h-3">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--terracotta)] opacity-40 animate-ping" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--terracotta)]" />
          </span>
          Current
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-[var(--forest)]" />Upcoming
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: STATUS_STYLE.past.fill }} />Past
        </span>
        <span className="ml-auto font-mono-acc tracking-wider text-[10px]">
          {touchedStates.size}/50 states on the journey
        </span>
      </div>
    </div>
  );
}
